/**
 * geocodingCache
 * Layer: shared/geocoding
 * Change: manejo-de-coordenadas-y-mapas
 *
 * Distance-based cache for reverse-geocoding results, persisted in localStorage.
 * Avoids re-hitting the geocoding API when the device hasn't moved meaningfully.
 * Never throws — storage reads/writes are wrapped in try/catch.
 */
import { reverseGeocodeUseCase, type ReverseGeocodeResult } from '@/shared/geocoding/reverseGeocodeUseCase';

const CACHE_KEY = 'mi_geocoding_cache';
const CACHE_DISTANCE_THRESHOLD_METERS = 50;
const EARTH_RADIUS_METERS = 6371000;

interface CachedGeocodingEntry {
  coordinates: { lat: number; lng: number };
  address: ReverseGeocodeResult;
  cachedAt: number;
}

function haversineDistanceMeters(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));

  return EARTH_RADIUS_METERS * c;
}

function readCache(): CachedGeocodingEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as CachedGeocodingEntry;
  } catch {
    return null;
  }
}

function writeCache(entry: CachedGeocodingEntry): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // ignore storage failures (quota, privacy mode, etc.)
  }
}

export async function resolveAddressCached(
  lat: number,
  lng: number,
): Promise<ReverseGeocodeResult | null> {
  const point = { lat, lng };
  const cached = readCache();

  if (cached && haversineDistanceMeters(cached.coordinates, point) < CACHE_DISTANCE_THRESHOLD_METERS) {
    return cached.address;
  }

  const address = await reverseGeocodeUseCase(lat, lng);

  if (address) {
    writeCache({ coordinates: point, address, cachedAt: Date.now() });
  }

  return address;
}
